import React from 'react';
import { useAuth } from '@/context/auth';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { LogOut, User } from 'lucide-react';
import { UserRole, hasAdminAccess, hasOrgAdminAccess, hasSuperAdminAccess } from '@/context/auth/types';
import RoleBadge from '@/components/RoleBadge';

interface UserMenuProps {
  collapsed: boolean;
}

const UserMenu: React.FC<UserMenuProps> = ({ collapsed }) => {
  const { user, logout } = useAuth();
  
  const displayName = user?.name || user?.email || 'User';
  const initials = displayName
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .substring(0, 2);
  
  // Use the highest role if multiple are present (JWT vs DB)
  const effectiveRole = user?.jwt_role || user?.role;
  
  return (
    <div className="flex flex-col gap-2">
      <div 
        className={cn(
          "flex items-center gap-3 p-2 rounded-md w-full",
          collapsed ? "justify-center" : "justify-start"
        )}
      >
        <Avatar className={cn("", collapsed ? "h-8 w-8" : "h-12 w-12")}>
          <AvatarFallback className={cn("bg-brand-yellow text-brand-dark-text", collapsed ? "text-sm" : "text-xl")}>
            {initials}
          </AvatarFallback>
        </Avatar>
        
        {!collapsed && (
          <div className="flex flex-col justify-center">
            <p className="text-sm font-medium leading-none">{displayName}</p>
            <p className="text-xs text-muted-foreground truncate max-w-[180px] mt-1">{user?.email}</p>
            {effectiveRole && (
              <div className="self-start mt-0">
                <RoleBadge role={effectiveRole} size="sm" />
              </div>
            )}
          </div>
        )}
      </div>
      
      <div className={cn("", collapsed ? "flex justify-center" : "")}>
        <button
          type="button"
          onClick={logout}
          className={cn(
            "flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors",
            "text-muted-foreground hover:bg-destructive/10 hover:text-destructive",
            collapsed ? "w-auto" : "w-full justify-start"
          )}
        >
          <LogOut className="h-4 w-4" />
          {!collapsed && <span>Logout</span>}
        </button>
      </div>
    </div>
  );
};

export default UserMenu;
